/*
 * BANYOMDA — MOBILE NATIVE RAIL TOUCH ENGINE v9
 * Purpose: make Banyomda's explicit mobile product rails independently swipeable
 * even when the theme's legacy swipe/Swiper helpers are present elsewhere.
 * Touch input remains native where possible; horizontal-dominant drags are
 * promoted to direct scrollLeft updates to prevent gesture dead-zones.
 */
(function () {
  const SELECTOR = '.banyomda-mobile-rail-viewport';
  const rails = new WeakMap();

  function bindRail(rail) {
    if (!rail || rails.has(rail)) return;

    const state = {
      x: 0,
      y: 0,
      scrollLeft: 0,
      active: false,
      locked: false,
      moved: false,
    };

    const isInteractive = (target) => Boolean(target?.closest?.('a, button, input, select, textarea, summary, [role=\"button\"]'));

    const start = (x, y) => {
      state.x = x;
      state.y = y;
      state.scrollLeft = rail.scrollLeft;
      state.active = true;
      state.locked = false;
      state.moved = false;
    };

    const move = (x, y, event) => {
      if (!state.active) return;
      const dx = x - state.x;
      const dy = y - state.y;

      if (!state.locked) {
        if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          state.active = false;
          return;
        }
        state.locked = true;
        state.moved = true;
        rail.classList.add('is-dragging');
      }

      if (state.locked) {
        event?.preventDefault?.();
        rail.scrollLeft = state.scrollLeft - dx;
      }
    };

    const end = () => {
      if (!state.active && !state.locked) return;
      state.active = false;
      state.locked = false;
      rail.classList.remove('is-dragging');
      // Keep `moved` alive until the synthetic click event arrives. The
      // click handler below consumes it and clears it on the same task.
    };

    rail.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      if (isInteractive(event.target)) return;
      const touch = event.touches[0];
      start(touch.clientX, touch.clientY);
    }, { passive: true });

    rail.addEventListener('touchmove', (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      move(touch.clientX, touch.clientY, event);
    }, { passive: false });

    rail.addEventListener('touchend', end, { passive: true });
    rail.addEventListener('touchcancel', end, { passive: true });

    // Desktop/Shopify device-emulation fallback: drag the rail with a mouse.
    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (isInteractive(event.target)) return;
      start(event.clientX, event.clientY);
      rail.setPointerCapture?.(event.pointerId);
    });

    rail.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      move(event.clientX, event.clientY, event);
    });

    rail.addEventListener('click', (event) => {
      if (!state.moved) return;
      event.preventDefault();
      event.stopPropagation();
      state.moved = false;
    }, true);

    // A fresh pointer/touch interaction must always begin with a clean click
    // state. This prevents a previous horizontal swipe from suppressing the
    // next legitimate tap on WhatsApp or Add-to-cart controls.
    const resetClickState = () => {
      state.moved = false;
    };

    rail.addEventListener('touchstart', resetClickState, { passive: true, capture: true });
    rail.addEventListener('pointerdown', resetClickState, { capture: true });

    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('lostpointercapture', end);

    rails.set(rail, state);
  }

  function scan(root = document) {
    root.querySelectorAll?.(SELECTOR).forEach(bindRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.(SELECTOR)) bindRail(node);
        scan(node);
      });
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
